/**
 * The brand-colour rules, extracted so the threshold cases can be asserted directly instead of
 * through a rendered form. `Zalo.tsx` has its own test for the wiring these cannot reach — a
 * zod refine message arriving as translated inline text.
 *
 * `BRAND_COLOR_PATTERN` mirrors the server's `src/integrations/zalo/branding.ts`. They are
 * separate codebases and cannot import from each other; the tests on both sides are what keeps
 * them honest, because a mismatch means the form accepts something the API then rejects.
 */
export const BRAND_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

/**
 * The platform's own brand colour, shown as the placeholder and as the preview's background
 * before the owner has chosen anything. It must be the same literal the server falls back to
 * (`apps/server/src/integrations/zalo/branding.ts`) or the empty-state preview advertises a
 * colour the exported config will not carry — and, worse, pairs it with `onPrimary('')`'s
 * white, which is only readable because this particular colour is dark.
 *
 * Pinned by brandColor.test.ts. See the comment in that server file for the full list of
 * copies and which of them are pinned.
 */
export const DEFAULT_BRAND_COLOR = '#A0673D';

export function isBrandColor(value: string): boolean {
  return BRAND_COLOR_PATTERN.test(value);
}

function relativeLuminance(hex: string): number {
  const channels = [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255);
  const linear = channels.map((channel) =>
    channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

function contrast(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

const LIGHT_TEXT = '#FFFFFF';
const DARK_TEXT = '#0D0D0D';

// Derived, not hand-computed: a typo'd literal here (0.006062, the luminance of #121212, not
// #0D0D0D) drifted from apps/zalo-mini/src/services/brand.ts and disagreed with it across a
// whole band of colours while both suites stayed green — see brandColor.test.ts's threshold
// cases.
const DARK_LUMINANCE = relativeLuminance(DARK_TEXT);

/** The same rule the Mini App applies, so the preview shows what a diner will actually see. */
export function onPrimary(hex: string): string {
  if (!isBrandColor(hex)) {
    return LIGHT_TEXT;
  }
  const luminance = relativeLuminance(hex);
  return contrast(luminance, 1) >= contrast(luminance, DARK_LUMINANCE) ? LIGHT_TEXT : DARK_TEXT;
}
